//@ts-check
import {emptyProductCombo, ProductComboSteps} from '@/types/productComboTypes'
import {createContext, useCallback, useContext, useMemo, useState} from 'react'
import {useLocation} from 'react-router-dom'

/**
 * @typedef {Object} ProductComboContextValue
 * @property {import('@/types/productComboTypes').ProductCombo} productCombo - Current product combo state.
 * @property {import('react').Dispatch<import('react').SetStateAction<import('@/types/productComboTypes').ProductCombo>>} setProductCombo - Setter for the product combination state.
 * @property {(updates: Partial<import('@/types/productComboTypes').ProductCombo>) => void} updateProductCombo - Shallow merge updater for the product combo state.
 * @property {number} currentStep - Current Product Combination step.
 * @property {import('react').Dispatch<import('react').SetStateAction<number>>} setCurrentStep - Setter for the current step.
 * @property {boolean} isDuplicating - Flag indicating if the product combo is being duplicated.
 */

/**
 * Default Product Combination context value.
 * @type {ProductComboContextValue}
 */
export const defaultProductComboContext = {
  productCombo: emptyProductCombo,
  setProductCombo: () => {},
  updateProductCombo: () => {},
  currentStep: ProductComboSteps.SETUP,
  setCurrentStep: () => {},
  isDuplicating: false,
}

/**
 * Product Combination context.
 * @type {import('react').Context<ProductComboContextValue | undefined>}
 */
export const ProductComboContext = createContext(/** @type {ProductComboContextValue | undefined} */ (undefined))

/**
 * Provides Product Combination state to its descendants.
 * @param {{
 *   children: import('react').ReactNode,
 *   initialState?: import('@/types/productComboTypes').ProductCombo,
 *   initialStep?: number,
 * }} props - Provider props.
 * @returns {JSX.Element} Product Combination context provider.
 */
export const ProductComboProvider = ({children, initialState, initialStep}) => {
  const location = useLocation()
  const navigationState = location.state || {}

  const [productCombo, setProductCombo] = useState(() => ({
    ...emptyProductCombo,
    ...(navigationState.productCombo || {}),
    ...(initialState || {}),
  }))
  const [currentStep, setCurrentStep] = useState(initialStep ?? navigationState.step ?? ProductComboSteps.SETUP)
  const updateProductCombo = useCallback(updates => {
    setProductCombo(previousProductCombo => ({
      ...previousProductCombo,
      ...updates,
    }))
  }, [])

  const isDuplicating = navigationState.isDuplicating

  const value = useMemo(
    () => ({
      productCombo,
      setProductCombo,
      updateProductCombo,
      currentStep,
      setCurrentStep,
      isDuplicating,
    }),
    [productCombo, currentStep, updateProductCombo, isDuplicating]
  )

  return <ProductComboContext.Provider value={value}>{children}</ProductComboContext.Provider>
}

/**
 * Product Combination context consumer hook.
 * @returns {ProductComboContextValue} Product Combination context value.
 */
export const useProductCombo = () => {
  const context = useContext(ProductComboContext)

  if (!context) {
    throw new Error('useProductCombo must be used within a ProductComboProvider')
  }

  return context
}
